<template>
  <div class="login-container">
    <div class="login-background">
      <div class="background-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
    </div>

    <div class="login-content">
      <div class="login-card">
        <div class="login-header">
          <div class="logo">
            <img src="@/assets/amana1.jpeg" alt="Inveep GPAO" class="logo-image" />
            <h1>Inveep GPAO</h1>
          </div>
          <p class="subtitle">Connectez-vous à votre espace d'administration</p>
        </div>

        <!-- Formulaire de connexion classique -->
        <form v-if="currentView === 'login'" @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="emailOrName">Email ou nom d'utilisateur</label>
            <div class="input-wrapper">
              <i class="fas fa-user"></i>
              <input
                id="emailOrName"
                v-model="form.emailOrName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.emailOrName }"
                placeholder="votre@email.com ou nom d'utilisateur"
                required
              />
            </div>
            <div v-if="errors.emailOrName" class="form-error">
              {{ errors.emailOrName }}
            </div>
          </div>

          <div class="form-group">
            <label for="password">Mot de passe</label>
            <div class="input-wrapper">
              <i class="fas fa-lock"></i>
              <input
                id="password"
                v-model="form.motDePasse"
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': errors.motDePasse }"
                placeholder="Votre mot de passe"
                required
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <div v-if="errors.motDePasse" class="form-error">
              {{ errors.motDePasse }}
            </div>
          </div>

          <div class="form-options">
            <label class="checkbox-wrapper">
              <input type="checkbox" v-model="rememberMe">
              <span class="checkmark"></span>
              Se souvenir de moi
            </label>
            <a href="#" class="forgot-link" @click.prevent="switchView('forgotPassword')">Mot de passe oublié ?</a>
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-lg login-btn"
            :disabled="loading"
          >
            <span v-if="loading" class="loading-spinner"></span>
            <i v-else class="fas fa-sign-in-alt"></i>
            {{ loading ? 'Connexion...' : 'Se connecter' }}
          </button>

          <div v-if="loginError" class="login-error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ loginError }}
          </div>
        </form>

        <!-- Formulaire de validation OTP suite à création de compte -->
        <form v-else-if="currentView === 'otpVerify'" @submit.prevent="handleVerifyOtp" class="login-form">
          <div class="form-section text-center mb-4">
            <h3 style="font-size: 18px; font-weight: 700; color: var(--primary-green);"><i class="fas fa-key"></i> Validation OTP</h3>
            <p style="font-size: 13px; color: var(--text-muted); margin-top: 8px;">
              Un code de validation à 6 chiffres a été envoyé par e-mail à :<br>
              <strong>{{ otpEmail }}</strong>
            </p>
          </div>

          <div class="form-group">
            <label for="verificationOtpCode">Entrez le code OTP reçu</label>
            <div class="input-wrapper text-center">
              <i class="fas fa-shield-alt"></i>
              <input
                id="verificationOtpCode"
                v-model="verificationOtpCode"
                type="text"
                maxlength="6"
                class="form-control text-center"
                placeholder="123456"
                style="letter-spacing: 8px; font-size: 18px; font-weight: 700;"
                required
              />
            </div>
          </div>

          <button type="submit" class="btn btn-primary btn-lg login-btn mt-3" :disabled="loading">
            <span v-if="loading" class="loading-spinner"></span>
            <i v-else class="fas fa-check-circle"></i>
            {{ loading ? 'Validation...' : 'Valider le compte' }}
          </button>

          <button type="button" class="btn btn-outline btn-block mt-3" @click="handleResendOtp" :disabled="loading">
            <i class="fas fa-sync-alt"></i> Renvoyer le code OTP
          </button>

          <button type="button" class="btn btn-link btn-block mt-2" @click="switchView('login')">
            Retour à la connexion
          </button>

          <div v-if="loginError" class="login-error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ loginError }}
          </div>
        </form>

        <!-- Formulaire de mot de passe oublié -->
        <form v-else-if="currentView === 'forgotPassword'" @submit.prevent="forgotStage === 1 ? submitForgotPassword() : submitResetPassword()" class="login-form">
          <div class="form-section text-center mb-4">
            <h3 style="font-size: 18px; font-weight: 700; color: var(--primary-green);"><i class="fas fa-unlock-alt"></i> Récupération de compte</h3>
            <p style="font-size: 13px; color: var(--text-muted); margin-top: 8px;">
              {{ forgotStage === 1 ? 'Saisissez votre e-mail pour recevoir le code de récupération.' : 'Entrez le code OTP reçu et votre nouveau mot de passe.' }}
            </p>
          </div>

          <!-- Étape 1 : Demande de code -->
          <div v-if="forgotStage === 1" class="form-group">
            <label for="forgotEmail">Votre adresse e-mail</label>
            <div class="input-wrapper">
              <i class="fas fa-envelope"></i>
              <input
                id="forgotEmail"
                v-model="forgotEmail"
                type="email"
                class="form-control"
                placeholder="votre@email.com"
                required
              />
            </div>
          </div>

          <!-- Étape 2 : Réinitialisation -->
          <div v-else>
            <div class="form-group">
              <label for="forgotOtpCode">Code OTP reçu</label>
              <div class="input-wrapper text-center">
                <i class="fas fa-key"></i>
                <input
                  id="forgotOtpCode"
                  v-model="forgotOtpCode"
                  type="text"
                  maxlength="6"
                  class="form-control text-center"
                  placeholder="123456"
                  style="letter-spacing: 8px; font-size: 18px; font-weight: 700;"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="forgotNewPassword">Nouveau mot de passe</label>
              <div class="input-wrapper">
                <i class="fas fa-lock"></i>
                <input
                  id="forgotNewPassword"
                  v-model="forgotNewPassword"
                  type="password"
                  class="form-control"
                  placeholder="Au moins 6 caractères"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label for="forgotConfirmPassword">Confirmer le mot de passe</label>
              <div class="input-wrapper">
                <i class="fas fa-check"></i>
                <input
                  id="forgotConfirmPassword"
                  v-model="forgotConfirmPassword"
                  type="password"
                  class="form-control"
                  placeholder="Confirmer"
                  required
                />
              </div>
            </div>
          </div>

          <button type="submit" class="btn btn-primary btn-lg login-btn mt-3" :disabled="loading">
            <span v-if="loading" class="loading-spinner"></span>
            <i v-else class="fas fa-paper-plane"></i>
            {{ loading ? 'Chargement...' : (forgotStage === 1 ? 'Envoyer le code' : 'Réinitialiser le mot de passe') }}
          </button>

          <button type="button" class="btn btn-link btn-block mt-3" @click="switchView('login')">
            Retour à la connexion
          </button>

          <div v-if="loginError" class="login-error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ loginError }}
          </div>
        </form>

        <div class="login-footer">
          <p class="help-text">
            Besoin d'aide ? Contactez votre administrateur système
          </p>
          <p class="signup-link-text" style="margin-top: 15px; font-size: 13px;">
            Nouvelle entreprise ? <router-link to="/signup" style="color: #10b981; font-weight: 600; text-decoration: none;">Créer un compte</router-link>
          </p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'

export default {
  name: 'Login',
  setup() {
    const store = useStore()
    const router = useRouter()
    const toast = useToast()

    const loading = ref(false)
    const showPassword = ref(false)
    const rememberMe = ref(false)
    const loginError = ref('')

    // Vues possibles: 'login', 'otpVerify', 'forgotPassword'
    const currentView = ref('login')

    const form = reactive({
      emailOrName: '',
      motDePasse: ''
    })

    const errors = reactive({
      emailOrName: '',
      motDePasse: ''
    })

    // Variables pour la validation OTP
    const otpEmail = ref('')
    const verificationOtpCode = ref('')

    // Variables pour mot de passe oublié
    const forgotEmail = ref('')
    const forgotOtpCode = ref('')
    const forgotNewPassword = ref('')
    const forgotConfirmPassword = ref('')
    const forgotStage = ref(1) // 1 = Saisie email, 2 = Saisie OTP + Nouveau mot de passe

    const validateForm = () => {
      errors.emailOrName = ''
      errors.motDePasse = ''
      
      if (!form.emailOrName) {
        errors.emailOrName = 'L\'email ou nom d\'utilisateur est requis'
        return false
      }
      
      if (form.emailOrName.length < 3) {
        errors.emailOrName = 'L\'email ou nom d\'utilisateur doit contenir au moins 3 caractères'
        return false
      }
      
      if (!form.motDePasse) {
        errors.motDePasse = 'Le mot de passe est requis'
        return false
      }
      
      if (form.motDePasse.length < 6) {
        errors.motDePasse = 'Le mot de passe doit contenir au moins 6 caractères'
        return false
      }
      
      return true
    }

    const handleLogin = async () => {
      loginError.value = ''
      
      if (!validateForm()) {
        return
      }

      loading.value = true

      try {
        const result = await store.dispatch('auth/login', {
          emailOrName: form.emailOrName,
          motDePasse: form.motDePasse
        })

        if (result.success) {
          toast.success(`Bienvenue ${result.user.prenom} 👋!`)
          router.push('/dashboard')
        }
      } catch (error) {
        if (error.requiresValidation) {
          toast.warning(error.message)
          otpEmail.value = error.email
          currentView.value = 'otpVerify'
        } else {
          loginError.value = error.message
          toast.error(error.message)
        }
      } finally {
        loading.value = false
      }
    }

    const handleVerifyOtp = async () => {
      if (verificationOtpCode.value.length !== 6) {
        toast.error('Le code OTP doit contenir 6 chiffres.')
        return
      }

      loading.value = true
      loginError.value = ''

      try {
        await store.dispatch('auth/verifyOtp', {
          email: otpEmail.value,
          otpCode: verificationOtpCode.value
        })
        toast.success('Votre compte administrateur a été validé !')
        router.push('/dashboard')
      } catch (error) {
        loginError.value = error.message
        toast.error(loginError.value)
      } finally {
        loading.value = false
      }
    }

    const handleResendOtp = async () => {
      loading.value = true
      try {
        await store.dispatch('auth/forgotPassword', { email: otpEmail.value })
        toast.success('Un nouveau code OTP a été envoyé à votre adresse e-mail.')
      } catch (error) {
        toast.error('Erreur lors du renvoi du code OTP.')
      } finally {
        loading.value = false
      }
    }

    const submitForgotPassword = async () => {
      if (!forgotEmail.value) {
        toast.error('Veuillez renseigner votre e-mail.')
        return
      }

      loading.value = true
      loginError.value = ''

      try {
        await store.dispatch('auth/forgotPassword', { email: forgotEmail.value })
        toast.success('Un e-mail de récupération contenant le code OTP a été envoyé.')
        forgotStage.value = 2
      } catch (error) {
        loginError.value = error.message
        toast.error(error.message)
      } finally {
        loading.value = false
      }
    }

    const submitResetPassword = async () => {
      if (!forgotOtpCode.value || !forgotNewPassword.value) {
        toast.error('Tous les champs sont requis.')
        return
      }

      if (forgotNewPassword.value !== forgotConfirmPassword.value) {
        toast.error('Les mots de passe ne correspondent pas.')
        return
      }

      loading.value = true
      loginError.value = ''

      try {
        await store.dispatch('auth/resetPassword', {
          email: forgotEmail.value,
          otpCode: forgotOtpCode.value,
          newPassword: forgotNewPassword.value
        })
        toast.success('Votre mot de passe a été réinitialisé avec succès.')
        currentView.value = 'login'
        forgotStage.value = 1
        forgotEmail.value = ''
        forgotOtpCode.value = ''
        forgotNewPassword.value = ''
        forgotConfirmPassword.value = ''
      } catch (error) {
        loginError.value = error.message
        toast.error(error.message)
      } finally {
        loading.value = false
      }
    }

    const switchView = (view) => {
      currentView.value = view
      loginError.value = ''
      if (view === 'forgotPassword') {
        forgotStage.value = 1
      }
    }

    return {
      form,
      errors,
      loading,
      showPassword,
      rememberMe,
      loginError,
      currentView,
      otpEmail,
      verificationOtpCode,
      forgotEmail,
      forgotOtpCode,
      forgotNewPassword,
      forgotConfirmPassword,
      forgotStage,
      handleLogin,
      handleVerifyOtp,
      handleResendOtp,
      submitForgotPassword,
      submitResetPassword,
      switchView
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  position: relative;
  overflow: hidden;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #10b981, #059669);
}

.background-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  animation: float 6s ease-in-out infinite;
}

.shape-1 {
  width: 200px;
  height: 200px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 300px;
  height: 300px;
  top: 60%;
  right: 10%;
  animation-delay: 2s;
}

.shape-3 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  left: 60%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.login-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
}

.login-card {
  background: var(--white);
  border-radius: 1.5rem;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 450px;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.logo-image {
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 12px;
}

.logo i {
  font-size: 2.5rem;
  color: var(--accent-orange);
}

.logo h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #10b981;
  margin: 0;
}

.subtitle {
  color: var(--medium-gray);
  font-size: 1rem;
  margin: 0;
}

.login-form {
  margin-bottom: 2rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper i {
  position: absolute;
  left: 1rem;
  color: var(--medium-gray);
  z-index: 1;
}

.input-wrapper .form-control {
  padding-left: 3rem;
  padding-right: 3rem;
}

.input-wrapper .form-control:focus {
  border-color: #10b981 !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15) !important;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--medium-gray);
  cursor: pointer;
  padding: 0.25rem;
  z-index: 1;
}

.password-toggle:hover {
  color: var(--dark-gray);
}

.form-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--medium-gray);
}

.checkbox-wrapper input[type="checkbox"] {
  display: none;
}

.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid var(--light-gray);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkmark {
  background-color: #10b981;
  border-color: #10b981;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkmark::after {
  content: '✓';
  color: var(--white);
  font-size: 0.75rem;
  font-weight: bold;
}

.login-btn {
  width: 100%;
  margin-bottom: 1rem;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981, #059669) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25) !important;
  font-weight: 600 !important;
  height: 48px;
  border-radius: 8px;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #059669, #047857) !important;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35) !important;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.login-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
  padding: 0.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.forgot-link {
  font-size: 0.875rem;
  color: #10b981;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.forgot-link:hover {
  color: #059669;
}

.btn-outline {
  background-color: transparent;
  border: 2px solid #10b981;
  color: #10b981;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-outline:hover {
  background-color: #10b981;
  color: white;
}

.btn-link {
  background: none;
  border: none;
  color: #64748b;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.875rem;
}

.btn-link:hover {
  color: #334155;
}

.btn-block {
  width: 100%;
}

.login-footer {
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid var(--light-gray);
}

.help-text {
  color: var(--medium-gray);
  font-size: 0.875rem;
  margin: 0;
}


/* Responsive */
@media (max-width: 768px) {
  .login-content {
    padding: 1rem;
  }

  .login-card {
    padding: 2rem;
  }
}
</style>
